/**
 * Admin Users Page
 * 
 * User management interface showing:
 * - Complete user list with search and filtering
 * - User details and account information
 * - Subscription status management
 * - User activity and reading progress
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Search, 
  Mail, 
  Calendar, 
  BookOpen,
  Clock,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  BarChart3,
  KeyRound,
  Loader2,
  Shield
} from "lucide-react";
import { Link } from "react-router-dom";
import { client } from "@/lib/api";
import { toast } from "sonner";

interface User {
  id: string;
  name: string;
  email: string;
  created_at: string;
  subscription_status: "free" | "premium";
  role: "user" | "admin" | "preview" | "educator";
  last_active?: string;
  total_reading_time: number;
  books_read: number;
  current_streak: number;
}

export default function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "free" | "premium" | "preview" | "educator">("all");
  const [loading, setLoading] = useState(true);
  
  // Password reset state
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetReason, setResetReason] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  
  // Role change state
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedRoleUser, setSelectedRoleUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState<"user" | "admin" | "preview" | "educator">("user");
  const [roleChangeReason, setRoleChangeReason] = useState("");
  const [isChangingRole, setIsChangingRole] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search and status
    let filtered = users;
    
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply status filter
    if (filterStatus !== "all") {
      if (filterStatus === "preview") {
        filtered = filtered.filter(user => user.role === "preview");
      } else if (filterStatus === "educator") {
        filtered = filtered.filter(user => user.role === "educator");
      } else {
        filtered = filtered.filter(user => user.subscription_status === filterStatus);
      }
    }
    
    setFilteredUsers(filtered);
  }, [users, searchQuery, filterStatus]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch("/api/admin/users", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        console.log("📊 Admin users loaded from PostgreSQL:", {
          userCount: data.users?.length || 0,
          dataSource: "PostgreSQL",
          sampleEmails: data.users?.slice(0, 3).map(u => u.email) || []
        });
      } else {
        console.error("❌ Failed to load users from PostgreSQL:", response.status, response.statusText);
        toast.error("Failed to load users from PostgreSQL");
      }
    } catch (error) {
      console.error("Users load error:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const formatReadingTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error("Please enter a new password");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setIsResetting(true);

    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const response = await fetch("/api/admin/reset-user-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          newPassword,
          reason: resetReason || `Password reset by admin for ${selectedUser.email}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Password successfully reset for ${selectedUser.email}`);
        setIsResetDialogOpen(false);
        setSelectedUser(null);
        setNewPassword("");
        setResetReason("");
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to reset password");
      }
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error("Failed to reset password");
    } finally {
      setIsResetting(false);
    }
  };

  const openResetDialog = (user: User) => {
    setSelectedUser(user);
    setNewPassword("");
    setResetReason("");
    setIsResetDialogOpen(true);
  };

  const openRoleDialog = (user: User) => {
    setSelectedRoleUser(user);
    setNewRole(user.role); // Pre-select current role
    setRoleChangeReason("");
    setIsRoleDialogOpen(true);
  };

  const handleChangeRole = async () => {
    if (!selectedRoleUser || !newRole) {
      toast.error("Please select a role");
      return;
    }

    // Check if role is actually changing
    if (newRole === selectedRoleUser.role) {
      toast.error("User already has this role");
      return;
    }

    setIsChangingRole(true);

    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const response = await fetch("/api/admin/change-user-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: selectedRoleUser.id,
          newRole,
          reason: roleChangeReason || `Role changed from ${selectedRoleUser.role} to ${newRole}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Role successfully changed for ${selectedRoleUser.email}`);
        
        // Update local state to reflect the change
        setUsers(prevUsers => 
          prevUsers.map(u => 
            u.id === selectedRoleUser.id ? { ...u, role: newRole } : u
          )
        );
        
        setIsRoleDialogOpen(false);
        setSelectedRoleUser(null);
        setNewRole("user");
        setRoleChangeReason("");
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to change role");
      }
    } catch (error) {
      console.error("Role change error:", error);
      toast.error("Failed to change role");
    } finally {
      setIsChangingRole(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">
            {filteredUsers.length} of {users.length} users
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <Button
                variant={filterStatus === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("all")}
              >
                All
              </Button>
              <Button
                variant={filterStatus === "free" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("free")}
              >
                Free
              </Button>
              <Button
                variant={filterStatus === "premium" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("premium")}
              >
                Premium
              </Button>
              <Button
                variant={filterStatus === "preview" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("preview")}
              >
                Preview
              </Button>
              <Button
                variant={filterStatus === "educator" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus("educator")}
              >
                Educator
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            All registered users and their account information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No users found matching your criteria</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-forest-100 rounded-full flex items-center justify-center">
                      <span className="text-forest-700 font-semibold text-lg">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-gray-900">{user.name}</h3>
                        <Badge variant={user.subscription_status === "premium" ? "default" : "secondary"}>
                          {user.subscription_status}
                        </Badge>
                        {user.role !== "user" && (
                          <Badge 
                            variant="outline" 
                            className={
                              user.role === "admin" 
                                ? "border-blue-500 text-blue-700 bg-blue-50" 
                                : user.role === "educator"
                                ? "border-green-500 text-green-700 bg-green-50"
                                : "border-orange-500 text-orange-700 bg-orange-50"
                            }
                          >
                            {user.role}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Joined {formatDate(user.created_at)}
                        </div>
                        
                        {user.last_active && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Active {formatDate(user.last_active)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right text-sm">
                      <div className="flex items-center gap-1 text-gray-600">
                        <BookOpen className="w-3 h-3" />
                        {user.books_read} books
                      </div>
                      <div className="flex items-center gap-1 text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatReadingTime(user.total_reading_time)}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/admin/user-analytics/${user.id}`}>
                          <BarChart3 className="w-4 h-4 mr-1" />
                          Analytics
                        </Link>
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openRoleDialog(user)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        Change Role
                      </Button>
                      
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openResetDialog(user)}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                      >
                        <KeyRound className="w-4 h-4 mr-1" />
                        Reset Password
                      </Button>
                      
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Role Change Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Change User Access Level
            </DialogTitle>
            <DialogDescription>
              {selectedRoleUser && (
                <>
                  Change the access level for <strong>{selectedRoleUser.name}</strong> ({selectedRoleUser.email}).
                  This will affect what they can access and do on the platform.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedRoleUser && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-600">Current Role:</div>
                <div className="font-medium text-gray-900 mt-1 capitalize">
                  {selectedRoleUser.role === "user" && "User (Full book access)"}
                  {selectedRoleUser.role === "preview" && "Preview (Chapter 1 only)"}
                  {selectedRoleUser.role === "admin" && "Admin (Platform management)"}
                  {selectedRoleUser.role === "educator" && "Educator (Teacher dashboard)"}
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <Label>New Access Level:</Label>
              <RadioGroup value={newRole} onValueChange={(value) => setNewRole(value as "user" | "admin" | "preview" | "educator")}>
                <div className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                  <RadioGroupItem value="user" id="role-user" disabled={isChangingRole} />
                  <div className="flex-1">
                    <Label htmlFor="role-user" className="font-medium cursor-pointer">
                      User
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Full access to all book content and features
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                  <RadioGroupItem value="preview" id="role-preview" disabled={isChangingRole} />
                  <div className="flex-1">
                    <Label htmlFor="role-preview" className="font-medium cursor-pointer">
                      Preview
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Limited access - Chapter 1 only (for demos and trials)
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                  <RadioGroupItem value="admin" id="role-admin" disabled={isChangingRole} />
                  <div className="flex-1">
                    <Label htmlFor="role-admin" className="font-medium cursor-pointer">
                      Admin
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Full platform management and user administration
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 transition-colors">
                  <RadioGroupItem value="educator" id="role-educator" disabled={isChangingRole} />
                  <div className="flex-1">
                    <Label htmlFor="role-educator" className="font-medium cursor-pointer">
                      Educator
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Teacher dashboard — class check-ins, SEL tracking, and growth insights
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="roleChangeReason">Reason (Optional)</Label>
              <Textarea
                id="roleChangeReason"
                value={roleChangeReason}
                onChange={(e) => setRoleChangeReason(e.target.value)}
                placeholder="Why are you changing this user's access level?"
                disabled={isChangingRole}
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsRoleDialogOpen(false)}
                disabled={isChangingRole}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangeRole}
                disabled={isChangingRole || !newRole || (selectedRoleUser && newRole === selectedRoleUser.role)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isChangingRole ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Confirm Change
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-orange-600" />
              Reset User Password
            </DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <>
                  Reset the password for <strong>{selectedUser.name}</strong> ({selectedUser.email}).
                  They will be able to log in immediately with the new password.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                disabled={isResetting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="resetReason">Reason (Optional)</Label>
              <Textarea
                id="resetReason"
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder="Why are you resetting this password?"
                disabled={isResetting}
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsResetDialogOpen(false)}
                disabled={isResetting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={isResetting || !newPassword || newPassword.length < 6}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                {isResetting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
